import { useState } from 'react'
import { Mail, Send } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import {
  useCreateInvitation,
  useInvitations,
  useResendInvitation,
  useRevokeInvitation,
  useRoles,
} from '@/features/team/queries'
import type { Invitation } from '@/features/team/types'

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleString() : '-')
const statusVariant = (s: string): 'success' | 'warning' | 'danger' | 'secondary' =>
  s === 'accepted' ? 'success' : s === 'pending' ? 'warning' : s === 'revoked' ? 'danger' : 'secondary'

// An invitation acknowledged anonymously on the email-link landing page is
// shown as "accepted" too, even though the membership is only bound once the
// invitee signs in or signs up (status flips to 'accepted' at that point).
const displayStatus = (i: Invitation) =>
  i.status === 'pending' && i.acknowledged_at ? 'accepted' : i.status

export function InvitationsTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const invitationsQ = useInvitations()
  const rolesQ = useRoles()
  const create = useCreateInvitation()
  const resend = useResendInvitation()
  const revoke = useRevokeInvitation()
  const canInvite = hasPermission('team.invite')
  const canManage = hasPermission('team.manage')

  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [roleId, setRoleId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [acceptLink, setAcceptLink] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<Invitation | null>(null)

  const submit = async () => {
    setError(null)
    if (!email.trim() || !roleId) {
      setError('Email and role are required.')
      return
    }
    try {
      const r = await create.mutateAsync({ email: email.trim(), roleId: Number(roleId) })
      setAcceptLink(r.acceptLink)
      toast({ title: 'Invitation sent.' })
    } catch (err) {
      setError(errMsg(err))
    }
  }

  const reset = () => {
    setEmail('')
    setRoleId('')
    setError(null)
    setAcceptLink(null)
  }

  const onResend = async (i: Invitation) => {
    try {
      const r = await resend.mutateAsync(i.id)
      if (r.acceptLink) {
        toast({ title: 'Invitation resent.', description: 'A fresh link has been sent.' })
      } else {
        toast({ title: 'Invitation resent.' })
      }
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const onRevoke = async (i: Invitation) => {
    try {
      await revoke.mutateAsync(i.id)
      toast({ title: 'Invitation revoked.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const invitations = invitationsQ.data?.invitations ?? []
  const roles = rolesQ.data?.roles ?? []

  const columns: ColumnDef<Invitation>[] = [
    { accessorKey: 'email', header: 'Email', cell: ({ row }) => <span className="font-semibold">{row.original.email}</span> },
    {
      accessorKey: 'role_name',
      header: 'Role',
      cell: ({ row }) => <Badge variant="secondary">{row.original.role_name ?? '-'}</Badge>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const s = displayStatus(row.original)
        return <Badge variant={statusVariant(s)}>{s}</Badge>
      },
    },
    {
      accessorKey: 'expires_at',
      header: 'Expires',
      cell: ({ row }) => <span className="text-xs text-muted-foreground">{fmtDate(row.original.expires_at)}</span>,
    },
    {
      accessorKey: 'invited_by_email',
      header: 'Invited by',
      cell: ({ row }) => row.original.invited_by_email ?? '-',
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const i = row.original
        // Hide row actions once the invitation is no longer actionable - this
        // includes the "acknowledged anonymously" state, where the workspace
        // owner shouldn't be tempted to revoke a row the invitee has already
        // committed to.
        if (displayStatus(i) !== 'pending') return null
        return (
          <div className="flex flex-wrap gap-1">
            {canInvite ? (
              <Button size="sm" variant="ghost" onClick={() => onResend(i)}>
                Resend
              </Button>
            ) : null}
            {canManage ? (
              <Button size="sm" variant="danger" onClick={() => setConfirm(i)}>
                Revoke
              </Button>
            ) : null}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-3">
      <Toolbar className="justify-end">
        {canInvite ? (
          <Button onClick={() => { reset(); setOpen(true) }}>
            <Send className="size-4" />
            Invite member
          </Button>
        ) : null}
      </Toolbar>
      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            rows={invitations}
            loading={invitationsQ.isLoading}
            empty="No invitations yet."
          />
        </CardContent>
      </Card>

      <Modal
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) reset() }}
        title="Invite team member"
        footer={
          acceptLink ? (
            <>
              <Button variant="secondary" onClick={reset}>
                Send another
              </Button>
              <Button onClick={() => setOpen(false)}>Done</Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={create.isPending}>
                Send invitation
              </Button>
            </>
          )
        }
      >
        {acceptLink ? (
          <div className="space-y-3">
            <Alert>
              <Mail className="size-4" />
              <AlertDescription>
                Invitation created. Share the accept link below if email delivery is delayed.
              </AlertDescription>
            </Alert>
            <div className="break-all rounded-md bg-muted p-2 font-mono text-xs">{acceptLink}</div>
          </div>
        ) : (
          <div className="space-y-3">
            <FormField
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />
            <FormField
              label="Role"
              control={
                <Select value={roleId} onValueChange={setRoleId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              }
            />
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}
      </Modal>

      <Modal.Confirm
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title="Revoke invitation"
        body={`Revoke the pending invitation for ${confirm?.email ?? ''}?`}
        confirmLabel="Revoke"
        confirmVariant="danger"
        onConfirm={async () => {
          const c = confirm
          setConfirm(null)
          if (c) await onRevoke(c)
        }}
      />
    </div>
  )
}
